import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Clock, Plus, Loader2, Pencil, Trash2, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { SortableTableHead, useSortableTable } from '../../components/SortableTableHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_DIA = [
  { value: 'normal', label: 'Dia Normal' },
  { value: 'compensado', label: 'Compensado' },
  { value: 'folga', label: 'Folga Obrigatória' }
];

const HorariosPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [horarios, setHorarios] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Hook para ordenação
  const { sortColumn, sortDirection, handleSort, sortData } = useSortableTable('codigo', 'asc');
  
  const [form, setForm] = useState({
    codigo: '',
    descricao: '',
    entrada1: '',
    saida1: '',
    entrada2: '',
    saida2: '',
    entrada3: '',
    saida3: '',
    horas_normais_diarias: 8,
    horas_intervalo_obrigatorio: 1,
    tolerancia_entrada: 10,
    tolerancia_saida: 10,
    tipo_dia: 'normal'
  });

  const fetchHorarios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/horarios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHorarios(data);
      }
    } catch (err) {
      toast.error('Erro ao carregar horários');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHorarios();
  }, [fetchHorarios]);

  const resetForm = () => {
    setForm({
      codigo: '',
      descricao: '',
      entrada1: '',
      saida1: '',
      entrada2: '',
      saida2: '',
      entrada3: '',
      saida3: '',
      horas_normais_diarias: 8,
      horas_intervalo_obrigatorio: 1,
      tolerancia_entrada: 10,
      tolerancia_saida: 10,
      tipo_dia: 'normal'
    });
    setEditando(null);
  };

  const handleNovoHorario = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditarHorario = (horario) => {
    setForm({
      codigo: horario.codigo || '',
      descricao: horario.descricao || '',
      entrada1: horario.entrada1 || '',
      saida1: horario.saida1 || '',
      entrada2: horario.entrada2 || '',
      saida2: horario.saida2 || '',
      entrada3: horario.entrada3 || '',
      saida3: horario.saida3 || '',
      horas_normais_diarias: horario.horas_normais_diarias || 8,
      horas_intervalo_obrigatorio: horario.horas_intervalo_obrigatorio || 1,
      tolerancia_entrada: horario.tolerancia_entrada || 10,
      tolerancia_saida: horario.tolerancia_saida || 10,
      tipo_dia: horario.tipo_dia || 'normal'
    });
    setEditando(horario);
    setDialogOpen(true);
  };

  const handleSalvar = async () => {
    if (!form.codigo || !form.descricao) {
      toast.error('Preencha código e descrição');
      return;
    }

    setSaving(true);
    try {
      const url = editando 
        ? `${API_URL}/api/horarios/${editando.id}`
        : `${API_URL}/api/horarios`;
      
      const res = await fetch(url, {
        method: editando ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editando ? 'Horário atualizado!' : 'Horário criado!');
        setDialogOpen(false);
        fetchHorarios();
        resetForm();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch (err) {
      toast.error('Erro ao salvar horário');
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (horario) => {
    if (!window.confirm(`Excluir o horário ${horario.codigo}?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/horarios/${horario.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Horário excluído!');
        fetchHorarios();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao excluir');
      }
    } catch (err) {
      toast.error('Erro ao excluir horário');
    }
  };

  const calcularHorasTrabalhadas = (h) => {
    const calcularMinutos = (entrada, saida) => {
      if (!entrada || !saida) return 0;
      const [he, me] = entrada.split(':').map(Number);
      const [hs, ms] = saida.split(':').map(Number);
      return (hs * 60 + ms) - (he * 60 + me);
    };

    const total = 
      calcularMinutos(h.entrada1, h.saida1) +
      calcularMinutos(h.entrada2, h.saida2) +
      calcularMinutos(h.entrada3, h.saida3);
    
    return (total / 60).toFixed(1);
  };

  const horariosFiltrados = sortData(horarios.filter(h =>
    h.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
  ));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Horários de Trabalho</h1>
          <p className="text-slate-500">Cadastre os horários disponíveis na empresa</p>
        </div>
        <Button onClick={handleNovoHorario} className="gap-2" data-testid="btn-novo-horario">
          <Plus className="w-4 h-4" />
          Novo Horário
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Lista de Horários</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead column="codigo" label="Código" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="descricao" label="Descrição" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="entrada1" label="Entrada 1" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="saida1" label="Saída 1" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="entrada2" label="Entrada 2" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="saida2" label="Saída 2" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="horas_normais_diarias" label="Horas" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHead column="tipo_dia" label="Tipo Dia" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {horariosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    {searchTerm ? 'Nenhum horário encontrado' : 'Nenhum horário cadastrado'}
                  </TableCell>
                </TableRow>
              ) : (
                horariosFiltrados.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono font-medium">{h.codigo}</TableCell>
                    <TableCell>{h.descricao}</TableCell>
                    <TableCell className="font-mono">{h.entrada1 || '-'}</TableCell>
                    <TableCell className="font-mono">{h.saida1 || '-'}</TableCell>
                    <TableCell className="font-mono">{h.entrada2 || '-'}</TableCell>
                    <TableCell className="font-mono">{h.saida2 || '-'}</TableCell>
                    <TableCell className="font-mono">{calcularHorasTrabalhadas(h)}h</TableCell>
                    <TableCell>
                      <Badge variant={h.tipo_dia === 'folga' ? 'secondary' : h.tipo_dia === 'compensado' ? 'outline' : 'default'}>
                        {TIPOS_DIA.find(t => t.value === h.tipo_dia)?.label || h.tipo_dia}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditarHorario(h)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleExcluir(h)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {editando ? 'Editar Horário' : 'Novo Horário'}
            </DialogTitle>
            <DialogDescription>
              Configure os horários de entrada e saída
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm({...form, codigo: e.target.value.toUpperCase()})}
                  placeholder="Ex: H01, H02"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Descrição *</Label>
                <Input
                  value={form.descricao}
                  onChange={(e) => setForm({...form, descricao: e.target.value})}
                  placeholder="Ex: Comercial 8h"
                />
              </div>
            </div>

            <div className="grid grid-cols-6 gap-2">
              <div>
                <Label className="text-xs">Entrada 1</Label>
                <Input type="time" value={form.entrada1} onChange={(e) => setForm({...form, entrada1: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Saída 1</Label>
                <Input type="time" value={form.saida1} onChange={(e) => setForm({...form, saida1: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Entrada 2</Label>
                <Input type="time" value={form.entrada2} onChange={(e) => setForm({...form, entrada2: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Saída 2</Label>
                <Input type="time" value={form.saida2} onChange={(e) => setForm({...form, saida2: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Entrada 3</Label>
                <Input type="time" value={form.entrada3} onChange={(e) => setForm({...form, entrada3: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Saída 3</Label>
                <Input type="time" value={form.saida3} onChange={(e) => setForm({...form, saida3: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Horas Normais</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.horas_normais_diarias}
                  onChange={(e) => setForm({...form, horas_normais_diarias: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Intervalo Obrigatório</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.horas_intervalo_obrigatorio}
                  onChange={(e) => setForm({...form, horas_intervalo_obrigatorio: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Toler. Entrada (min)</Label>
                <Input
                  type="number"
                  value={form.tolerancia_entrada}
                  onChange={(e) => setForm({...form, tolerancia_entrada: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Toler. Saída (min)</Label>
                <Input
                  type="number"
                  value={form.tolerancia_saida}
                  onChange={(e) => setForm({...form, tolerancia_saida: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label>Tipo de Dia</Label>
              <Select value={form.tipo_dia} onValueChange={(v) => setForm({...form, tipo_dia: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DIA.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSalvar} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editando ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HorariosPage;
